import React from "react";
import { Mail, MapPin, Clock, Briefcase, Users, MessageSquareText, Building2, School, Heart } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { PageHero, Section } from "@/components/blocks";
import EnquiryForm from "@/components/EnquiryForm";

const TYPES = [
  { value: "Workshop", label: "Workshop", icon: Users },
  { value: "Career Support", label: "Career Support", icon: Briefcase },
  { value: "Interview Coaching", label: "Interview Coaching", icon: MessageSquareText },
  { value: "School Partnership", label: "School Partnership", icon: School },
  { value: "University Partnership", label: "University", icon: Building2 },
  { value: "Tutoring", label: "Tutoring", icon: Heart },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact & Scheduling"
        title="Let's start a conversation."
        subtitle="Whether you're a future teacher, a school, a university or a family — choose your enquiry type and we'll respond within one business day."
        image="https://images.pexels.com/photos/8419197/pexels-photo-8419197.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />
      <Section>
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Card className="p-6 sm:p-8 border-slate-200">
              <h2 className="font-display text-2xl font-bold text-slate-900 mb-1">Send an enquiry</h2>
              <p className="text-slate-500 mb-6">Select the option that best fits your needs.</p>
              <Tabs defaultValue="Workshop">
                <TabsList className="flex flex-nowrap overflow-x-auto justify-start h-auto gap-1 bg-slate-100 p-1 w-full" data-testid="contact-tabs">
                  {TYPES.map((t) => (
                    <TabsTrigger key={t.value} value={t.value} className="text-xs sm:text-sm data-[state=active]:bg-white" data-testid={`contact-tab-${t.value}`}>
                      <t.icon className="w-3.5 h-3.5 mr-1.5" /> {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {TYPES.map((t) => (
                  <TabsContent key={t.value} value={t.value} className="mt-6">
                    <EnquiryForm type={t.value} context={`${t.label} enquiry`} testPrefix={`contact-${t.value.replace(/\s/g, "-").toLowerCase()}`} />
                  </TabsContent>
                ))}
              </Tabs>
            </Card>
          </div>

          <div className="space-y-4">
            <Card className="p-6 border-slate-200">
              <h3 className="font-display font-bold text-slate-900 mb-4">Get in touch</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex gap-3"><Mail className="w-5 h-5 text-teal-600 shrink-0" /><div><div className="font-medium text-slate-800">Email</div><a href="mailto:admin@teachingpathways.com.au" className="text-slate-500 hover:text-teal-600 break-all">admin@teachingpathways.com.au</a></div></li>
                <li className="flex gap-3"><Mail className="w-5 h-5 text-teal-600 shrink-0" /><div><div className="font-medium text-slate-800">Direct lines</div><div className="text-slate-500 space-y-0.5 text-xs"><div>Workshops · workshops@teachingpathways.com.au</div><div>Schools &amp; universities · partnerships@teachingpathways.com.au</div><div>Tutoring · tutoring@teachingpathways.com.au</div><div>Orders &amp; downloads · resources@teachingpathways.com.au</div></div></div></li>
                <li className="flex gap-3"><MapPin className="w-5 h-5 text-teal-600 shrink-0" /><div><div className="font-medium text-slate-800">Based in</div><span className="text-slate-500">Australia · Serving globally</span></div></li>
                <li className="flex gap-3"><Clock className="w-5 h-5 text-teal-600 shrink-0" /><div><div className="font-medium text-slate-800">Response time</div><span className="text-slate-500">Within 1 business day</span></div></li>
              </ul>
            </Card>
            <Card className="p-6 bg-slate-900 text-white border-slate-800">
              <h3 className="font-display font-bold mb-2">Book a consultation</h3>
              <p className="text-sm text-slate-300 mb-4">Prefer to talk it through? Request a call and we'll find a time that works for you.</p>
              <div className="text-xs text-slate-400">Use the enquiry form and select your topic — we'll send scheduling options by email.</div>
            </Card>
          </div>
        </div>
      </Section>
    </div>
  );
}
